/**
 * /c/:characterId/decorate — fan-side scene decorator.
 *
 * Per `fan-scene-customization` spec. Composes the operator base scene
 * with the fan's overlay layer, lets the fan add owned library objects,
 * transform-manipulate them (gizmo: left-drag translate / right-drag
 * rotate / wheel scale), and persist the overlay back to DDB.
 *
 * Architecture
 * ────────────
 *  - Mount shader controller exactly the way Character3DStage does
 *    (sequential GLB loads, applies viewport pose + render params).
 *  - Re-attach base + overlay objects on every meaningful state edit.
 *    The runtime is the source of truth for what's visible; React state
 *    is the source of truth for what to save.
 *  - Selecting an overlay item arms the controller's TransformGizmo.
 *    On gizmo change, copy the new transform into React state.
 *  - Undo stack: ring buffer of last 20 mutations (add / remove /
 *    transform). Wiped on save + on page reload.
 *  - Navigation guard: `beforeunload` + React Router blocker prompt
 *    when there are unsaved changes.
 *
 * Mobile note
 * ───────────
 * The transform gizmo is mouse-driven on the controller side (left-
 * drag / right-drag / wheel). For touch-only devices we surface a
 * small form (X/Y/Z inputs + rotation + uniform scale) below the
 * canvas as a fallback. v2 can add real two-finger gestures.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getMyScene,
  getPublicInfo,
  listMyFanObjects,
  listMyOwnedCharacters,
  putMyScene,
  type FanSceneItem,
  type OwnedObject,
  type PublicCharacterInfo,
} from '../api/fan-api';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { loadSiblingMorphPack } from '../embed/runtime/model3d-morph-pack';

type Phase =
  | { kind: 'loading' }
  | { kind: 'not-owned' }
  | { kind: 'ready'; info: PublicCharacterInfo; baseItems: FanSceneItem[]; initialOverlay: FanSceneItem[]; owned: OwnedObject[] };

const ORIGIN_TRANSFORM: FanSceneItem['transform'] = {
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: [1, 1, 1],
};

function newOverlayId(): string {
  // crypto.randomUUID is available in modern browsers; fall back to a
  // simple time+random combo for older WebView contexts.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `fan-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function DecorateScene() {
  const { characterId } = useParams<{ characterId: string }>();
  const navigate = useNavigate();
  useDocumentTitle('布置房間');

  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [overlay, setOverlay] = useState<FanSceneItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Undo stack — snapshots of overlay BEFORE each mutation.
  const undoRef = useRef<FanSceneItem[][]>([]);
  const lastSavedRef = useRef<FanSceneItem[]>([]);

  const hostRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<unknown>(null);

  // ── Load phase: ownership, info, owned objects, overlay ──────────
  useEffect(() => {
    if (!characterId) return;
    let alive = true;
    void (async () => {
      try {
        const [ownedCharacters, info, ownedObjects, mine] = await Promise.all([
          listMyOwnedCharacters().catch(() => ({ items: [] })),
          getPublicInfo(characterId),
          listMyFanObjects().catch(() => ({ items: [] })),
          getMyScene(characterId).catch(() => ({ items: [] })),
        ]);
        if (!alive) return;
        const owns = ownedCharacters.items.some((it) => it.characterId === characterId);
        if (!owns) {
          setPhase({ kind: 'not-owned' });
          return;
        }
        // The composed sceneObjects from public-info contains both
        // operator base AND the fan's existing overlay (when authed +
        // owner). Subtract the overlay (matched by id) to recover the
        // pure base list — we re-attach base separately so the
        // selectable / non-selectable distinction is clean.
        const composed = (info.model3d?.sceneObjects ?? []) as FanSceneItem[];
        const overlayIds = new Set(mine.items.map((it) => it.id));
        const baseItems = composed.filter((s) => !overlayIds.has(s.id));
        setOverlay(mine.items);
        lastSavedRef.current = mine.items;
        setPhase({ kind: 'ready', info, baseItems, initialOverlay: mine.items, owned: ownedObjects.items });
      } catch (e) {
        if (alive) setPhase({ kind: 'not-owned' });
        console.error('[decorate] load failed', (e as Error).message);
      }
    })();
    return () => { alive = false; };
  }, [characterId]);

  // ── Mount the 3D controller once when phase=ready ────────────────
  useEffect(() => {
    if (phase.kind !== 'ready') return;
    const host = hostRef.current;
    if (!host) return;
    const info = phase.info;
    if (!info.model3d?.modelPath) return;
    let cancelled = false;
    let disposer: (() => void) | null = null;

    void import('@nxorbit/model3d-runtime').then(async (mod) => {
      if (cancelled) return;
      try {
        const controller = mod.createShaderComposed3DController({
          animationMap: info.model3d!.animationMap,
          transparent: true,
        });
        controllerRef.current = controller;
        disposer = () => controller.dispose();
        const loadedFallback = info.model3d!.morphPack
          ? null
          : await loadSiblingMorphPack(info.model3d!.modelPath).catch(() => null);
        if (cancelled) return;
        const pack = info.model3d!.texturePack as never | undefined;
        const morphPack = (info.model3d!.morphPack ?? loadedFallback?.pack) as never | undefined;
        await controller.mount(host, {
          url: info.model3d!.modelPath,
          format: info.model3d!.modelFormat,
          texturePack: pack,
          morphPack,
          expressionMode: morphPack ? 'morphTargets' : info.model3d!.expressionMode,
        } as never);
        if (cancelled) { controller.dispose(); return; }
        const cAny = controller as unknown as {
          setViewportRotationY?: (r: number) => void;
          setRenderParams?: (p: Record<string, number>) => void;
          setDefaultClipLabels?: (labels: string[]) => void;
        };
        if (info.model3d!.defaultClipLabels?.length) cAny.setDefaultClipLabels?.(info.model3d!.defaultClipLabels);
        if (info.model3d!.viewportPose?.rotationY !== undefined) cAny.setViewportRotationY?.(info.model3d!.viewportPose.rotationY);
        if (info.model3d!.renderParams) cAny.setRenderParams?.(info.model3d!.renderParams);

        // Attach operator base first, then fan overlay.
        const attachLoop = controller as unknown as {
          attachSceneObject?: (spec: FanSceneItem) => Promise<void>;
        };
        for (const obj of phase.baseItems) {
          if (cancelled) break;
          try { await attachLoop.attachSceneObject?.(obj); } catch (e) {
            console.warn('[decorate] base attach failed', { id: obj.id, err: (e as Error).message });
          }
        }
        for (const obj of phase.initialOverlay) {
          if (cancelled) break;
          try { await attachLoop.attachSceneObject?.(obj); } catch (e) {
            console.warn('[decorate] overlay attach failed', { id: obj.id, err: (e as Error).message });
          }
        }
      } catch (err) {
        console.error('[decorate] mount failed', err);
      }
    });

    return () => {
      cancelled = true;
      disposer?.();
      controllerRef.current = null;
    };
    // We deliberately only re-mount when characterId changes; per-item
    // attach/detach happens via dedicated effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.kind, characterId]);

  // ── Gizmo arming on selection ────────────────────────────────────
  useEffect(() => {
    const c = controllerRef.current as null | {
      attachTransformGizmo: (opts: { objectId: string; onChange: (t: FanSceneItem['transform']) => void }) => void;
      detachTransformGizmo: () => void;
      setOrbitControlsEnabled: (v: boolean) => void;
    };
    if (!c) return;
    if (!selectedId) {
      try { c.detachTransformGizmo(); c.setOrbitControlsEnabled(true); } catch { /* ignore */ }
      return;
    }
    try {
      c.setOrbitControlsEnabled(false);
      c.attachTransformGizmo({
        objectId: selectedId,
        onChange: (t) => {
          setOverlay((prev) => prev.map((it) => (it.id === selectedId ? { ...it, transform: t } : it)));
        },
      });
    } catch (e) {
      console.warn('[decorate] gizmo arm failed', (e as Error).message);
    }
    return () => {
      try { c.detachTransformGizmo(); c.setOrbitControlsEnabled(true); } catch { /* ignore */ }
    };
  }, [selectedId]);

  // ── Mutation helpers (push undo snapshot before each change) ─────
  const pushUndo = useCallback((snapshot: FanSceneItem[]) => {
    undoRef.current.push(snapshot);
    if (undoRef.current.length > 20) undoRef.current.shift();
  }, []);

  const addObject = useCallback(async (obj: OwnedObject) => {
    if (phase.kind !== 'ready') return;
    if (overlay.length >= 16) {
      setSaveError('此角色的個人物件已達上限（16）');
      return;
    }
    if (!obj.modelPath || !obj.modelFormat) {
      setSaveError('此物件缺少模型資料，無法擺放');
      return;
    }
    pushUndo(overlay);
    const next: FanSceneItem = {
      id: newOverlayId(),
      libraryId: obj.objectId,
      label: obj.label ?? '物件',
      modelPath: obj.modelPath,
      modelFormat: obj.modelFormat,
      kind: 'static',
      visibility: 'always',
      transform: { ...ORIGIN_TRANSFORM, position: [0, 0, 0] },
    };
    setOverlay((p) => [...p, next]);
    setSelectedId(next.id);
    const c = controllerRef.current as null | { attachSceneObject?: (s: FanSceneItem) => Promise<void> };
    try { await c?.attachSceneObject?.(next); } catch (e) {
      console.warn('[decorate] add attach failed', (e as Error).message);
    }
  }, [phase, overlay, pushUndo]);

  const removeObject = useCallback((id: string) => {
    pushUndo(overlay);
    setOverlay((p) => p.filter((it) => it.id !== id));
    if (selectedId === id) setSelectedId(null);
    const c = controllerRef.current as null | { detachSceneObject?: (id: string) => void };
    try { c?.detachSceneObject?.(id); } catch { /* ignore */ }
  }, [overlay, pushUndo, selectedId]);

  const undo = useCallback(async () => {
    const snapshot = undoRef.current.pop();
    if (!snapshot) return;
    // Diff snapshot vs current overlay to know what to attach / detach.
    const currIds = new Set(overlay.map((it) => it.id));
    const snapIds = new Set(snapshot.map((it) => it.id));
    const c = controllerRef.current as null | {
      attachSceneObject?: (s: FanSceneItem) => Promise<void>;
      detachSceneObject?: (id: string) => void;
      setSceneObjectTransform?: (id: string, t: FanSceneItem['transform']) => void;
    };
    // Remove items that exist now but not in snapshot.
    for (const it of overlay) {
      if (!snapIds.has(it.id)) {
        try { c?.detachSceneObject?.(it.id); } catch { /* ignore */ }
      }
    }
    // Add items that are in snapshot but not now.
    for (const it of snapshot) {
      if (!currIds.has(it.id)) {
        try { await c?.attachSceneObject?.(it); } catch { /* ignore */ }
      } else {
        // Restore transform if it changed.
        try { c?.setSceneObjectTransform?.(it.id, it.transform); } catch { /* ignore */ }
      }
    }
    setOverlay(snapshot);
    setSelectedId(null);
  }, [overlay]);

  // ── Ctrl+Z keybind ───────────────────────────────────────────────
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        void undo();
      } else if (e.key === 'Escape') {
        setSelectedId(null);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo]);

  // ── Unsaved-changes guard ────────────────────────────────────────
  const dirty = useMemo(() => {
    // Compare overlay vs lastSavedRef by length first, then deep stringify
    // (small N ≤16 so JSON compare is fine).
    if (overlay.length !== lastSavedRef.current.length) return true;
    return JSON.stringify(overlay) !== JSON.stringify(lastSavedRef.current);
  }, [overlay]);

  useEffect(() => {
    if (!dirty) return;
    function onBeforeUnload(e: BeforeUnloadEvent): void {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // ── Save ─────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!characterId) return;
    setSaving(true);
    setSaveError(null);
    try {
      await putMyScene(characterId, overlay);
      lastSavedRef.current = overlay;
      undoRef.current = [];
      setSavedAt(Date.now());
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }, [characterId, overlay]);

  const handleBack = useCallback(() => {
    if (dirty && !window.confirm('尚未儲存的擺設將會消失，確定離開嗎？')) return;
    navigate(`/c/${characterId}`);
  }, [characterId, dirty, navigate]);

  // ── Rendering ────────────────────────────────────────────────────
  if (phase.kind === 'loading') {
    return (
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4">
        <div className="rounded-2xl border border-slate-800 nx-skeleton-shimmer h-[60vh]" aria-hidden="true" />
      </div>
    );
  }
  if (phase.kind === 'not-owned') {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="text-xl font-semibold mb-2">無法布置</h1>
        <p className="text-slate-400 text-sm mb-6">你必須先加入這個角色才能擺放物件。</p>
        <button
          type="button"
          onClick={() => navigate(`/c/${characterId}`)}
          className="rounded-full bg-violet-500 px-4 py-1.5 text-white text-sm hover:bg-violet-400"
        >
          回角色頁
        </button>
      </div>
    );
  }

  const selected = overlay.find((it) => it.id === selectedId);

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-4 lg:h-[calc(100dvh-4rem)]">
      {/* 3D viewport */}
      <section className="relative rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 overflow-hidden aspect-[16/10] sm:aspect-[16/9] lg:aspect-auto lg:h-full min-h-[240px]">
        <div ref={hostRef} className="absolute inset-0" aria-label="3D 編輯畫布" />
        {/* HUD: top-left back, top-right save */}
        <div className="absolute top-3 left-3 z-10 flex gap-2">
          <button
            type="button"
            onClick={handleBack}
            className="rounded-full bg-slate-950/70 px-3 py-1.5 text-xs text-slate-100 backdrop-blur hover:bg-slate-900/80"
            aria-label="返回角色頁"
          >
            ← 返回
          </button>
          <button
            type="button"
            onClick={() => { void undo(); }}
            disabled={undoRef.current.length === 0}
            className="rounded-full bg-slate-950/70 px-3 py-1.5 text-xs text-slate-100 backdrop-blur hover:bg-slate-900/80 disabled:opacity-40"
            aria-label="復原"
          >
            ⤺ 復原
          </button>
        </div>
        <div className="absolute top-3 right-3 z-10 flex gap-2 items-center">
          {dirty && <span className="text-[11px] text-amber-300 bg-slate-950/70 px-2 py-1 rounded-full backdrop-blur">未儲存</span>}
          {savedAt && !dirty && <span className="text-[11px] text-emerald-300 bg-slate-950/70 px-2 py-1 rounded-full backdrop-blur">已儲存</span>}
          <button
            type="button"
            onClick={() => { void handleSave(); }}
            disabled={saving || !dirty}
            className="rounded-full bg-violet-500 hover:bg-violet-400 disabled:bg-slate-700 disabled:text-slate-500 px-4 py-1.5 text-sm font-medium text-white shadow-lg"
          >
            {saving ? '儲存中…' : '儲存'}
          </button>
        </div>
        {saveError && (
          <div role="alert" className="absolute bottom-3 left-3 right-3 z-10 rounded-lg border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-200">
            {saveError}
          </div>
        )}
        {selected && (
          <div className="absolute bottom-3 left-3 z-10 rounded-lg bg-slate-950/80 backdrop-blur px-3 py-2 text-xs text-slate-200 max-w-[80%]">
            <div className="font-medium">已選取：{selected.label}</div>
            <div className="text-slate-400 mt-0.5">左鍵拖移 · 右鍵旋轉 · 滾輪縮放 · Esc 取消</div>
          </div>
        )}
      </section>

      {/* Sidebar: inventory + placed list */}
      <aside className="flex flex-col gap-3 lg:overflow-y-auto lg:max-h-full">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-3 sm:p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold">我的物件</h2>
            <span className="text-[11px] text-slate-500">{overlay.length} / 16</span>
          </div>
          {phase.owned.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-700 bg-slate-900/40 p-3 text-center text-slate-500 text-xs">
              還沒有可擺放的物件。先到「物件庫」買幾個。
            </div>
          )}
          {phase.owned.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {phase.owned.map((obj) => (
                <button
                  key={obj.objectId}
                  type="button"
                  onClick={() => { void addObject(obj); }}
                  disabled={!obj.modelPath || overlay.length >= 16}
                  className="aspect-square rounded-lg border border-slate-700 bg-slate-950 flex items-center justify-center text-2xl text-slate-600 hover:border-violet-500/60 hover:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={obj.label}
                >
                  📦
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-3 sm:p-4">
          <h2 className="text-sm font-semibold mb-2">已擺放</h2>
          {overlay.length === 0 && (
            <div className="text-xs text-slate-500 leading-relaxed">
              點上方「我的物件」加進場景，再點下方的物件選取它，用左鍵拖移、右鍵旋轉、滾輪縮放擺到喜歡的位置。
            </div>
          )}
          {overlay.length > 0 && (
            <ul className="space-y-1.5">
              {overlay.map((it) => (
                <li
                  key={it.id}
                  className={`rounded-lg border px-2.5 py-2 text-xs flex items-center justify-between gap-2 ${
                    it.id === selectedId
                      ? 'border-violet-500 bg-violet-500/10 text-slate-100'
                      : 'border-slate-700 bg-slate-900/60 text-slate-300 hover:border-slate-600'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setSelectedId((s) => (s === it.id ? null : it.id))}
                    className="flex-1 text-left truncate"
                  >
                    {it.label}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeObject(it.id)}
                    className="rounded-full bg-rose-500/20 hover:bg-rose-500/40 px-2 py-0.5 text-[11px] text-rose-200"
                    aria-label="移除"
                  >
                    移除
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-3 text-[11px] text-slate-500 leading-relaxed">
          <div className="text-slate-400 font-medium mb-1">小提示</div>
          <div>· 角色身邊已存在的物件是經營者放的，你無法移動它們。</div>
          <div>· 你擺的物件只有你自己看得到。</div>
          <div>· 按 Ctrl + Z 復原上一步；最多保留 20 步。</div>
        </div>
      </aside>
    </div>
  );
}
